// S3 · Step 2 — Catalogue (`/onboarding/catalogue`, Module 02 §4 S3, §3.3).
//
// Two ways to fill a catalogue, both real:
//
//  * BY HAND — the Module 01 catalogue screens, EMBEDDED, not copied. This
//    renders the very same `ProductsListScreen` that serves `/catalogue/products`
//    (template-picker-first create flow prefilled from the pack's attribute
//    schema, filters, bulk lifecycle, cost redaction, mobile cards), so there is
//    exactly one product list in the product and it cannot drift. Opening a
//    product from here goes to the Module 01 detail route — which owns the BOM
//    and routing editors — and the step rail brings the user back.
//
//  * FROM A SPREADSHEET — the §3.3 import pipeline: template → upload →
//    presigned PUT → parse, then S4 for mapping, checking and loading.
//
// Above both: the four `catalogue.*` go-live checks this step moves, so progress
// is a number and not a feeling (§1 job 3).
//
// The step's own scratch payload is tiny — which tab the admin was last on —
// but it goes through the same `PUT /steps/2/payload` optimistic-concurrency
// path as every other step, so two admins editing step 2 get the §9.1 conflict
// panel rather than a silent overwrite.

import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { components } from "@assessly/api-client";
import { ProductsListScreen } from "../../office/catalogue/screens/products-list";
import { Tabs } from "../../../components/tabs";
import { SaveContinueBar } from "../components/save-continue-bar";
import { StepContainer } from "../components/step-container";
import { useOnboardingSession } from "../hooks/onboarding-queries";
import { useOnboardingPermissions } from "../hooks/permissions";
import { useTerm } from "../hooks/terminology";
import { isWizardFrozen, stepStateOf } from "../lib/wizard-steps";
import { CatalogueProgress } from "./catalogue-progress";
import { CATALOGUE_IMPORT_KINDS, type ImportKind } from "./imports/import-model";
import { ImportJobsTable } from "./imports/jobs-table";
import { ImportUploadPanel } from "./imports/upload-panel";
import "./imports/imports.css";

type S = components["schemas"];

type Tab = "manual" | "import";

const TABS: readonly { id: Tab; labelKey: string }[] = [
  { id: "manual", labelKey: "onboarding.catalogueStep.tabs.manual" },
  { id: "import", labelKey: "onboarding.catalogueStep.tabs.import" },
];

/** Products a page of the embedded list shows. The step stacks its progress
 * card and its tabs above the catalogue list, and at the list's own 50 rows
 * it measured 4,452 px at 1440 x 900, over W-SWEEP's four screens (design-
 * system §3.17); 20 rows keep it under. */
export const CATALOGUE_STEP_PAGE_SIZE = 20;

export function CatalogueStepScreen() {
  const { t } = useTranslation();
  const { term } = useTerm();
  const perms = useOnboardingPermissions();
  const session = useOnboardingSession();

  const step = stepStateOf(session.data?.steps ?? [], 2);
  const frozen = session.data !== undefined && isWizardFrozen(session.data.session);

  const [tab, setTab] = useState<Tab>("manual");
  const [seededFrom, setSeededFrom] = useState<string | null>(null);

  // Re-seed the tab from the server's step payload the first time it arrives,
  // and again after a STALE_PAYLOAD reload, so the wizard resumes where the
  // admin left it (§3.1 "closing the browser loses nothing").
  useEffect(() => {
    if (step === undefined || seededFrom === step.id) return;
    setSeededFrom(step.id);
    setTab(tabOf(step.payload));
  }, [step, seededFrom]);

  const terms = {
    product: term("product"),
    bom: term("bom"),
    routingStep: term("routing_step"),
  };

  function reseed(serverStep: S["OnboardingStepState"]) {
    setTab(tabOf(serverStep.payload));
  }

  return (
    <StepContainer
      stepNo={2}
      titleKey="onboarding.steps.catalogue.title"
      blurbKey="onboarding.steps.catalogue.blurb"
      footer={
        <SaveContinueBar
          stepNo={2}
          getPayload={() => ({ tab })}
          updatedAt={step?.updated_at}
          // The DERIVED scratch, not the raw payload: a never-saved step's
          // payload is `{}`::jsonb, which never equals the seeded `{tab}` —
          // passing it raw read "Unsaved changes" on every untouched first
          // visit and armed the leave guard (§3.8).
          loadedPayload={step === undefined ? undefined : { tab: tabOf(step.payload) }}
          onStale={reseed}
          continueTo="/onboarding/plant"
          frozen={frozen}
        />
      }
    >
      <CatalogueProgress frozen={frozen} />

      <Tabs
        label={t("onboarding.catalogueStep.tabs.label")}
        tabs={TABS.map((entry) => ({
          id: entry.id,
          domId: `catalogue-tab-${entry.id}`,
          label: t(entry.labelKey),
        }))}
        active={tab}
        onSelect={(id) => {
          setTab(id === "import" ? "import" : "manual");
        }}
        controls={`catalogue-panel-${tab}`}
      />

      {tab === "manual" ? (
        <section
          className="catalogue-manual"
          role="tabpanel"
          id="catalogue-panel-manual"
          aria-labelledby="catalogue-tab-manual"
        >
          <h3>{t("onboarding.catalogueStep.manual.title")}</h3>
          <p className="wizard-step-blurb">{t("onboarding.catalogueStep.manual.blurb", terms)}</p>
          <p className="wizard-save-bar-note">
            {t("onboarding.catalogueStep.manual.note", terms)}{" "}
            <Link to="/catalogue/products">
              {t("onboarding.catalogueStep.manual.openCatalogue")}
            </Link>
          </p>
          {/* The Module 01 screen itself — embedded, never forked. `embedded`
              drops its parent breadcrumb only: inside a wizard step a
              "Catalogue /" link is a way out of the flow, not navigation. */}
          <div className="wizard-embed">
            <ProductsListScreen embedded pageSize={CATALOGUE_STEP_PAGE_SIZE} />
          </div>
        </section>
      ) : (
        <section
          className="catalogue-import"
          role="tabpanel"
          id="catalogue-panel-import"
          aria-labelledby="catalogue-tab-import"
        >
          <ImportUploadPanel
            kinds={CATALOGUE_IMPORT_KINDS}
            canImport={(kind: ImportKind) => perms.canImport(kind)}
          />
          <ImportJobsTable
            kinds={CATALOGUE_IMPORT_KINDS}
            canRun={(kind: ImportKind) => perms.canImport(kind)}
          />
        </section>
      )}
    </StepContainer>
  );
}

/** Read the saved tab out of the step's free-form payload without trusting it. */
function tabOf(payload: Record<string, unknown> | undefined): Tab {
  return payload?.["tab"] === "import" ? "import" : "manual";
}
